// A table of runs, one way wherever runs are listed: a project's Runs tab and the Runs page (every project's). Each row
// is a run (its number, then its name and status, as every record's table starts), its units, built, dates and what its
// units cost (the Landed cost tab's total, an estimate until some are built). `useRunRows()` works the rows out once
// per change; `runColumns()` gives the columns, with the project's when the table spans projects.
import { useMemo } from "react";
import { runLandedCents, runLandedRows, runResult } from "../../../../../shared/costs.ts";
import type { ProductionRun, Project } from "../../../../../shared/db.gen.ts";
import { pastRunText, RUN_STATUS_LABELS, RUN_STATUSES, type RunStatus } from "../../../../../shared/runs.ts";
import { centsText, shortDate, useCostIndex } from "../../../lib/costs.ts";
import { qty } from "../../../lib/format.ts";
import { Badge, cx } from "../../../ui/controls.tsx";
import type { Column } from "../../../ui/DataTable.tsx";

export const RUN_TONE: Record<RunStatus, "accent" | "warn" | "ok" | "neutral"> = {
  planning: "neutral",
  ordering: "accent",
  in_production: "warn",
  shipping: "accent",
  received: "ok",
  closed: "neutral",
};

export type RunRow = {
  id: string;
  run: ProductionRun;
  res: ReturnType<typeof runResult>;
  planned: number;
  built: number;
  received: number;
  /** What its units cost (the Landed cost tab's total), cents. */
  cost: number;
};

/** The rows for these runs, newest first. */
export function useRunRows(runs: readonly ProductionRun[]): RunRow[] {
  const cx_ = useCostIndex();
  return useMemo(
    () =>
      [...runs]
        .sort((a, b) => b.created_at.localeCompare(a.created_at))
        .map((r) => {
          const res = runResult(cx_, r.id);
          const sum = (k: "planned" | "built" | "received") => (res ? res.variants.reduce((n, v) => n + (res.units.get(v)?.[k] ?? 0), 0) : 0);
          return {
            id: r.id,
            run: r,
            res,
            planned: sum("planned"),
            built: sum("built"),
            received: sum("received"),
            cost: runLandedCents(runLandedRows(cx_, r.id)),
          };
        }),
    [cx_, runs],
  );
}

export const runStatusText = (r: ProductionRun) => (r.historical ? "Past run" : RUN_STATUS_LABELS[r.status]);

/** The columns of a table of runs; `projects` adds the project's (on the Runs page, where runs of every project show). */
export function runColumns({
  projects,
  cmName,
}: {
  projects?: ReadonlyMap<string, Pick<Project, "name" | "code">>;
  cmName?: (id: string | null) => string;
} = {}): Column<RunRow>[] {
  return [
    {
      key: "number",
      header: "Run",
      width: "90px",
      value: (x) => x.run.number,
      cell: (x) => <span className="font-mono text-[12px] font-medium">{x.run.number}</span>,
    },
    {
      key: "name",
      header: "Name",
      width: "minmax(160px,1fr)",
      value: (x) => x.run.name,
      cell: (x) => <span className="min-w-0 truncate">{x.run.name || <span className="text-faint">No name</span>}</span>,
    },
    {
      key: "status",
      header: "Status",
      width: "110px",
      value: (x) => (x.run.historical ? RUN_STATUSES.length : RUN_STATUSES.indexOf(x.run.status)),
      text: (x) => runStatusText(x.run),
      cell: (x) =>
        x.run.historical ? (
          <span title={pastRunText(x.run)}>
            <Badge tone="neutral">Past run</Badge>
          </span>
        ) : (
          <Badge tone={RUN_TONE[x.run.status]}>{RUN_STATUS_LABELS[x.run.status]}</Badge>
        ),
    },
    ...(projects
      ? [
          {
            key: "project",
            header: "Project",
            width: "minmax(140px,0.8fr)",
            value: (x: RunRow) => projects.get(x.run.project_id)?.name ?? "",
            cell: (x: RunRow) => <span className="truncate">{projects.get(x.run.project_id)?.name}</span>,
          } satisfies Column<RunRow>,
        ]
      : []),
    ...(cmName
      ? [
          {
            key: "cm",
            header: "CM",
            width: "110px",
            value: (x: RunRow) => cmName(x.run.cm_vendor_id),
            cell: (x: RunRow) => <span className="truncate">{cmName(x.run.cm_vendor_id)}</span>,
          } satisfies Column<RunRow>,
        ]
      : []),
    {
      key: "units",
      header: "Units",
      width: "90px",
      align: "right",
      value: (x) => x.planned,
      cell: (x) => (
        <span className="font-mono text-[12px]" title={`${qty(x.planned)} planned · ${qty(x.built)} built · ${qty(x.received)} received at HHL`}>
          {qty(x.planned)}
        </span>
      ),
    },
    {
      key: "built",
      header: "Built",
      width: "90px",
      align: "right",
      value: (x) => x.built,
      cell: (x) => (x.built > 0 ? <span className="font-mono text-[12px]">{qty(x.built)}</span> : <span className="text-faint">—</span>),
    },
    {
      key: "received",
      header: "Received",
      headerTitle: "Received at HHL",
      width: "90px",
      align: "right",
      optional: true,
      value: (x) => x.received,
      cell: (x) => <span className="font-mono text-[12px]">{qty(x.received)}</span>,
    },
    {
      key: "dates",
      header: "Ships · arrives",
      width: "210px",
      value: (x) => (x.run.historical ? (x.run.finished_on ?? "") : (x.run.target_ship ?? x.run.target_arrive ?? "")),
      text: (x) => (x.run.historical ? pastRunText(x.run) : `${shortDate(x.run.target_ship) || "—"} · ${shortDate(x.run.target_arrive) || "—"}`),
      cell: (x) => (
        <span className="truncate text-[12px] text-muted">
          {x.run.historical ? pastRunText(x.run) : `${shortDate(x.run.target_ship) || "—"} · ${shortDate(x.run.target_arrive) || "—"}`}
        </span>
      ),
    },
    {
      // What the units cost (the Landed cost tab's): the plan's estimate until some are built
      key: "cost",
      header: "Cost of the units",
      headerTitle: "Every unit's landed cost × its units (an estimate until some are built: the Landed cost tab)",
      width: "130px",
      align: "right",
      value: (x) => (x.res ? x.cost / 100 : null),
      text: (x) => (x.res ? `${centsText(x.cost)}${x.built > 0 ? "" : " est."}` : ""),
      cell: (x) =>
        x.res && (
          <span className={cx("font-mono text-[12px]", x.built === 0 && "text-muted")}>
            {centsText(x.cost)}
            {x.built === 0 && " est."}
          </span>
        ),
    },
  ];
}
